import { message } from "antd";
import { makeAutoObservable, runInAction } from "mobx";
import { CompanionRoomApi } from "./CompanionRoomApi";
import {
  buildCompanionOutboxStorageKey,
  persistCompanionOutboxFromState,
  readCompanionOutbox,
} from "./CompanionRoomOutboxStorage";
import type {
  CompanionMessageRow,
  CompanionOutboundRetryPayload,
  CompanionRoomFileRow,
  CompanionSidePanelKey,
  MyMessageThreadRow,
  PullNewCompanionMessagesResult,
  RejectedEventRow,
  SendCompanionMessageResult,
} from "./CompanionRoomType";

const POLL_MIN_MS = 2000;
const POLL_MAX_MS = 8000;
const POLL_STEP_MS = 1000;

function cmpMsgId(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

/** 公屏展示顺序：发送时间升序，同时间再按 id */
function cmpChatOrder(a: CompanionMessageRow, b: CompanionMessageRow): number {
  const ta = a.sentAt ? new Date(a.sentAt).getTime() : 0;
  const tb = b.sentAt ? new Date(b.sentAt).getTime() : 0;
  if (ta !== tb) return ta - tb;
  return cmpMsgId(a.id, b.id);
}

function companionMessageIsMine(m: CompanionMessageRow, userId: string): boolean {
  const u = userId.trim();
  if (!u) return false;
  return Boolean(m.fromUser) && (m.senderUserId?.trim() ?? "") === u;
}

function maxMessageId(list: CompanionMessageRow[]): string | undefined {
  if (list.length === 0) return undefined;
  return [...list].sort((x, y) => cmpMsgId(x.id, y.id)).at(-1)?.id;
}

/** 心跳拉取游标：忽略本地乐观消息的 id */
function maxServerMessageId(list: CompanionMessageRow[]): string | undefined {
  const serverOnly = list.filter((m) => !m.id.startsWith("local-"));
  return maxMessageId(serverOnly);
}

function applyRejectedEvents(
  approved: CompanionMessageRow[],
  events: RejectedEventRow[]
): { approved: CompanionMessageRow[]; changed: boolean } {
  if (!events.length) return { approved, changed: false };
  let changed = false;
  const byId = new Map(approved.map((item) => [item.id, item] as const));
  for (const event of events) {
    const current = byId.get(event.messageId);
    if (!current) continue;
    if (event.eventType === "SHIELD") {
      if (current.shielded) continue;
      byId.set(event.messageId, { ...current, shielded: true, deleted: false });
      changed = true;
      continue;
    }
    if (event.eventType === "RECALL") {
      byId.delete(event.messageId);
      changed = true;
    }
  }
  if (!changed) return { approved, changed: false };
  return {
    approved: Array.from(byId.values()).sort((a, b) => cmpChatOrder(a, b)),
    changed: true,
  };
}


export class CompanionRoomModel {
  spaceId = "";
  userId = "";
  /** 右侧抽屉：我的发言 / 学习资料 / 置顶列表 */
  sidePanel: CompanionSidePanelKey | null = null;

  loading = false;
  loadError = false;
  roomEnabled = false;
  chatRoomId = "";
  roomTitle = "";
  chatOpen = true;
  maxPerMinute: number | null = null;
  maxPerDay: number | null = null;
  remainPerDay: number | null = null;

  /** 群聊公屏 */
  chatMessages: CompanionMessageRow[] = [];
  hasOlderChat = false;
  loadingOlderChat = false;
  maxRejectedCursor = "";

  /** 轮询 */
  pollTimer: ReturnType<typeof setTimeout> | null = null;
  polling = false;
  pollDelayMs = POLL_MIN_MS;

  /** 我的发言 */
  mineThreads: MyMessageThreadRow[] = [];
  mineLoading = false;
  mineLoadingMore = false;
  mineHasMore = false;
  mineCursorSentAt: string | null = null;
  mineCursorMessageId: string | null = null;

  /** 学习资料 */
  pdfFiles: CompanionRoomFileRow[] = [];
  filesLoading = false;
  filesLoadingMore = false;
  filesHasMore = false;
  filesCursorSentAt: string | null = null;
  filesCursorMessageId: string | null = null;

  /** 置顶 */
  pinnedList: CompanionMessageRow[] = [];
  pinnedLoading = false;

  /** 本地外发失败时，按 message.id 保存重试入参（非 Observable，随 replace 维护） */
  private outboundRetryByMessageId = new Map<string, CompanionOutboundRetryPayload>();

  /** 发送中 */
  sending = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * 本条是否仍为「本地未成功送达」外发（失败待重试或正在重试中）。
   * 此种消息公屏不展示「引用」等与回复相关的侧栏操作。
   */
  hasUndeliveredOutboundRetry(messageId: string): boolean {
    return this.outboundRetryByMessageId.has(messageId.trim());
  }

  stopPoll(): void {
    this.polling = false;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  startPoll(): void {
    this.stopPoll();
    this.polling = true;
    this.pollDelayMs = POLL_MIN_MS;
    this.scheduleNextPoll();
  }

  private scheduleNextPoll(): void {
    if (!this.polling) return;
    this.pollTimer = setTimeout(() => {
      void this.runPollTick();
    }, this.pollDelayMs);
  }

  private adjustPollDelay(gotNew: boolean): void {
    if (gotNew) {
      this.pollDelayMs = POLL_MIN_MS;
      return;
    }
    this.pollDelayMs = Math.min(POLL_MAX_MS, this.pollDelayMs + POLL_STEP_MS);
  }

  private async runPollTick(): Promise<void> {
    if (!this.polling || !this.chatRoomId) return;
    const gotNew = await this.pullChatDelta();
    void this.refreshPinned({ silent: true });
    this.adjustPollDelay(gotNew);
    this.scheduleNextPoll();
  }

  /** 单次心跳拉取；若 hasMore 则连续拉直到追平 */
  private async pullChatDelta(): Promise<boolean> {
    if (!this.chatRoomId || !this.spaceId) return false;
    let anyNew = false;
    try {
      let localMaxApproved = maxServerMessageId(this.chatMessages);
      let localRejectedCursor = this.maxRejectedCursor;

      for (;;) {
        const res = await CompanionRoomApi.pullNewMessagesAndHeartbeat({
          spaceId: this.spaceId,
          chatRoomId: this.chatRoomId,
          maxApprovedOrSelfMessageId: localMaxApproved ?? null,
          maxRejectedId: localRejectedCursor || null,
        });

        const rejected = res.rejectedEvents ?? [];
        const hasNewMessages = (res.list?.length ?? 0) > 0;
        const hasRejected = rejected.length > 0;
        if (hasNewMessages || hasRejected) {
          anyNew = true;
        }

        runInAction(() => {
          if (hasNewMessages) {
            const have = new Set(this.chatMessages.map((x) => x.id));
            const add = (res.list ?? []).filter(
              (x) => !have.has(x.id) && (!x.deleted || companionMessageIsMine(x, this.userId))
            );
            if (add.length) {
              this.chatMessages = [...this.chatMessages, ...add].sort((a, b) => cmpChatOrder(a, b));
            }
          }
          const merged = applyRejectedEvents(this.chatMessages, rejected);
          this.chatMessages = merged.approved;
          if (res.maxRejectedId != null && res.maxRejectedId !== "") {
            this.maxRejectedCursor = res.maxRejectedId;
            localRejectedCursor = res.maxRejectedId;
          }
          this.applySpeakQuotaFromPull(res);
        });

        localMaxApproved = maxServerMessageId(this.chatMessages);

        if (!res.hasMore) break;
      }
    } catch {
      /* silent API */
    }
    return anyNew;
  }

  async init(spaceId: string, userId = ""): Promise<void> {
    this.stopPoll();
    const sid = spaceId.trim();
    const uid = userId.trim();
    this.spaceId = sid;
    this.userId = uid;
    if (!sid) return;

    runInAction(() => {
      this.loading = true;
      this.loadError = false;
      this.chatRoomId = "";
      this.chatMessages = [];
      this.hasOlderChat = false;
      this.mineThreads = [];
      this.pdfFiles = [];
      this.pinnedList = [];
      this.sidePanel = null;
      this.outboundRetryByMessageId.clear();
    });

    try {
      const info = await CompanionRoomApi.getRoomInfo({ spaceId: sid });
      if (!info.enabled) {
        runInAction(() => {
          this.roomEnabled = false;
          this.roomTitle = info.roomTitle?.trim() || "";
        });
        return;
      }
      const cid = info.chatRoomId?.trim() ?? "";
      runInAction(() => {
        this.roomEnabled = true;
        this.chatRoomId = cid;
        this.roomTitle = info.roomTitle?.trim() || "聊天室";
        this.chatOpen = info.chatOpen !== false;
        this.maxPerMinute = info.maxPerMinute ?? null;
        this.maxPerDay = info.maxPerDay ?? null;
        this.remainPerDay = info.remainPerDay ?? null;
      });
      if (!cid) return;

      await CompanionRoomApi.enterRoom({ spaceId: sid, chatRoomId: cid });
      const latest = await CompanionRoomApi.getLatestMessages({
        spaceId: sid,
        chatRoomId: cid,
        size: 50,
      });
      runInAction(() => {
        this.chatMessages = (latest.list ?? [])
          .filter((m) => !m.deleted || companionMessageIsMine(m, uid))
          .sort((a, b) => cmpChatOrder(a, b));
        this.hasOlderChat = latest.hasOlder === true;
        if (latest.maxRejectedId) {
          this.maxRejectedCursor = latest.maxRejectedId;
        } else {
          this.maxRejectedCursor = "";
        }
      });
      this.mergePersistedOutbox();
      this.startPoll();
      void this.refreshPinned({ silent: true });
    } catch (e) {
      message.error(e instanceof Error ? e.message : "加载失败");
      runInAction(() => {
        this.loadError = true;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  toggleSidePanel(key: CompanionSidePanelKey): void {
    if (this.sidePanel === key) {
      this.sidePanel = null;
      return;
    }
    this.sidePanel = key;
    if (!this.chatRoomId || !this.spaceId) return;
    if (key === "mine") {
      void this.loadMineFirstPage();
    }
    if (key === "files" && this.pdfFiles.length === 0 && !this.filesLoading) {
      void this.loadFilesFirstPage();
    }
    if (key === "pinned") {
      void this.refreshPinned({ silent: true });
    }
  }

  async loadOlderChat(): Promise<void> {
    if (!this.chatRoomId || !this.spaceId || !this.hasOlderChat) return;
    const first = this.chatMessages[0];
    if (!first) return;
    if (this.loadingOlderChat) return;
    runInAction(() => {
      this.loadingOlderChat = true;
    });
    try {
      const res = await CompanionRoomApi.getOlderMessages({
        spaceId: this.spaceId,
        chatRoomId: this.chatRoomId,
        anchorMessageId: first.id,
        size: 20,
      });
      runInAction(() => {
        const older = res.list ?? [];
        const have = new Set(this.chatMessages.map((m) => m.id));
        const add = older.filter(
          (m) => !have.has(m.id) && (!m.deleted || companionMessageIsMine(m, this.userId))
        );
        this.chatMessages = [...add, ...this.chatMessages].sort((a, b) => cmpChatOrder(a, b));
        this.hasOlderChat = res.hasMore === true;
      });
    } finally {
      runInAction(() => {
        this.loadingOlderChat = false;
      });
    }
  }

  private applySpeakQuotaFromPull(res: PullNewCompanionMessagesResult): void {
    if (res.maxPerDay !== undefined && res.maxPerDay !== null) {
      this.maxPerDay = res.maxPerDay;
    }
    if (res.remainPerDay !== undefined) {
      this.remainPerDay = res.remainPerDay ?? null;
    }
  }

  private applySpeakQuotaFromSend(res: SendCompanionMessageResult): void {
    if (res.maxPerDay !== undefined && res.maxPerDay !== null) {
      this.maxPerDay = res.maxPerDay;
    }
    if (res.remainPerDay !== undefined) {
      this.remainPerDay = res.remainPerDay ?? null;
    }
  }

  private makeLocalOutboundId(): string {
    return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private buildOptimisticUserMessage(
    tempId: string,
    partial: Pick<CompanionMessageRow, "messageType" | "content" | "fileUrl" | "fileName" | "replyToMessageId">
  ): CompanionMessageRow {
    return {
      id: tempId,
      chatRoomId: this.chatRoomId,
      messageType: partial.messageType,
      content: partial.content ?? "",
      fileUrl: partial.fileUrl ?? null,
      fileName: partial.fileName ?? null,
      fromUser: true,
      senderName: null,
      visibility: "PUBLIC",
      replyToMessageId: partial.replyToMessageId ?? null,
      shielded: false,
      deleted: false,
      sentAt: new Date().toISOString(),
      outboundFailed: false,
    };
  }

  private applySendSuccessReplace(tempId: string, serverMessage: CompanionMessageRow): void {
    this.outboundRetryByMessageId.delete(tempId);
    this.persistOutbox();
    const { outboundFailed: _of, ...real } = serverMessage;
    void _of;
    const dup = this.chatMessages.some((m) => m.id === real.id && m.id !== tempId);
    if (dup) {
      this.chatMessages = this.chatMessages.filter((m) => m.id !== tempId).sort(cmpChatOrder);
      return;
    }
    const hit = this.chatMessages.some((m) => m.id === tempId);
    if (hit) {
      this.chatMessages = this.chatMessages
        .map((m) => (m.id === tempId ? real : m))
        .sort(cmpChatOrder);
      return;
    }
    this.chatMessages = [...this.chatMessages, real].sort(cmpChatOrder);
  }

  private markOutboundFailed(tempId: string, payload: CompanionOutboundRetryPayload): void {
    this.outboundRetryByMessageId.set(tempId, payload);
    this.chatMessages = this.chatMessages.map((m) =>
      m.id === tempId ? { ...m, outboundFailed: true } : m
    );
    this.persistOutbox();
  }

  async retryOutboundMessage(messageId: string): Promise<void> {
    const payload = this.outboundRetryByMessageId.get(messageId);
    if (!payload || !this.chatRoomId || !this.spaceId) return;
    const now = new Date().toISOString();
    runInAction(() => {
      this.chatMessages = this.chatMessages
        .map((m) => (m.id === messageId ? { ...m, outboundFailed: false, sentAt: now } : m))
        .sort(cmpChatOrder);
      this.sending = true;
    });
    try {
      if (payload.kind === "TEXT") {
        const res = await CompanionRoomApi.sendMessage({
          spaceId: this.spaceId,
          chatRoomId: this.chatRoomId,
          messageType: "TEXT",
          visibility: "PUBLIC",
          content: payload.content,
          replyToMessageId: payload.replyToMessageId,
        });
        runInAction(() => {
          this.applySendSuccessReplace(messageId, res.message);
          this.applySpeakQuotaFromSend(res);
        });
      } else {
        const res = await CompanionRoomApi.sendMessage({
          spaceId: this.spaceId,
          chatRoomId: this.chatRoomId,
          messageType: "IMAGE",
          visibility: "PUBLIC",
          content: "",
          fileUrl: payload.fileUrl,
          fileName: payload.fileName,
          replyToMessageId: payload.replyToMessageId,
        });
        runInAction(() => {
          this.applySendSuccessReplace(messageId, res.message);
          this.applySpeakQuotaFromSend(res);
        });
      }
    } catch {
      runInAction(() => {
        this.markOutboundFailed(messageId, payload);
      });
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  async sendText(opts: { content: string; replyToMessageId?: string }): Promise<void> {
    if (!this.chatRoomId || !this.spaceId) return;
    const text = opts.content.trim();
    if (!text) {
      message.warning("请输入内容");
      return;
    }
    if (!this.chatOpen) {
      message.warning("当前不可发言");
      return;
    }
    const replyToMessageId = opts.replyToMessageId?.trim() || undefined;
    const tempId = this.makeLocalOutboundId();
    const optimistic = this.buildOptimisticUserMessage(tempId, {
      messageType: "TEXT",
      content: text,
      replyToMessageId,
    });
    runInAction(() => {
      this.outboundRetryByMessageId.delete(tempId);
      this.chatMessages = [...this.chatMessages, optimistic].sort(cmpChatOrder);
      this.sending = true;
    });
    try {
      const res = await CompanionRoomApi.sendMessage({
        spaceId: this.spaceId,
        chatRoomId: this.chatRoomId,
        messageType: "TEXT",
        visibility: "PUBLIC",
        content: text,
        replyToMessageId,
      });
      runInAction(() => {
        this.applySendSuccessReplace(tempId, res.message);
        this.applySpeakQuotaFromSend(res);
      });
    } catch {
      runInAction(() => {
        this.markOutboundFailed(tempId, { kind: "TEXT", content: text, replyToMessageId });
      });
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  async sendImage(opts: {
    fileUrl: string;
    fileName: string;
    replyToMessageId?: string;
  }): Promise<void> {
    if (!this.chatRoomId || !this.spaceId) return;
    if (!this.chatOpen) {
      message.warning("当前不可发言");
      return;
    }
    const replyToMessageId = opts.replyToMessageId?.trim() || undefined;
    const tempId = this.makeLocalOutboundId();
    const optimistic = this.buildOptimisticUserMessage(tempId, {
      messageType: "IMAGE",
      content: "",
      fileUrl: opts.fileUrl,
      fileName: opts.fileName,
      replyToMessageId,
    });
    runInAction(() => {
      this.outboundRetryByMessageId.delete(tempId);
      this.chatMessages = [...this.chatMessages, optimistic].sort(cmpChatOrder);
      this.sending = true;
    });
    try {
      const res = await CompanionRoomApi.sendMessage({
        spaceId: this.spaceId,
        chatRoomId: this.chatRoomId,
        messageType: "IMAGE",
        visibility: "PUBLIC",
        content: "",
        fileUrl: opts.fileUrl,
        fileName: opts.fileName,
        replyToMessageId,
      });
      runInAction(() => {
        this.applySendSuccessReplace(tempId, res.message);
        this.applySpeakQuotaFromSend(res);
      });
    } catch {
      runInAction(() => {
        this.markOutboundFailed(tempId, {
          kind: "IMAGE",
          fileUrl: opts.fileUrl,
          fileName: opts.fileName,
          replyToMessageId,
        });
      });
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  async loadMineFirstPage(): Promise<void> {
    if (!this.chatRoomId || !this.spaceId) return;
    runInAction(() => {
      this.mineLoading = true;
      this.mineThreads = [];
      this.mineCursorSentAt = null;
      this.mineCursorMessageId = null;
    });
    try {
      const res = await CompanionRoomApi.getMyMessagesWithReplies({
        spaceId: this.spaceId,
        chatRoomId: this.chatRoomId,
        size: 20,
      });
      runInAction(() => {
        this.mineThreads = res.list ?? [];
        this.mineHasMore = res.hasMore === true;
        this.mineCursorSentAt = res.nextCursorSentAt ?? null;
        this.mineCursorMessageId = res.nextCursorMessageId ?? null;
      });
    } catch {
      /* post 已 toast */
    } finally {
      runInAction(() => {
        this.mineLoading = false;
      });
    }
  }

  async loadMoreMine(): Promise<void> {
    if (!this.chatRoomId || !this.spaceId || !this.mineHasMore) return;
    if (this.mineLoadingMore || !this.mineCursorSentAt || !this.mineCursorMessageId) return;
    runInAction(() => {
      this.mineLoadingMore = true;
    });
    try {
      const res = await CompanionRoomApi.getMyMessagesWithReplies({
        spaceId: this.spaceId,
        chatRoomId: this.chatRoomId,
        cursorSentAt: this.mineCursorSentAt,
        cursorMessageId: this.mineCursorMessageId,
        size: 20,
      });
      runInAction(() => {
        const next = res.list ?? [];
        this.mineThreads = [...this.mineThreads, ...next];
        this.mineHasMore = res.hasMore === true;
        this.mineCursorSentAt = res.nextCursorSentAt ?? null;
        this.mineCursorMessageId = res.nextCursorMessageId ?? null;
      });
    } finally {
      runInAction(() => {
        this.mineLoadingMore = false;
      });
    }
  }

  async loadFilesFirstPage(): Promise<void> {
    if (!this.chatRoomId || !this.spaceId) return;
    runInAction(() => {
      this.filesLoading = true;
      this.pdfFiles = [];
      this.filesCursorSentAt = null;
      this.filesCursorMessageId = null;
    });
    try {
      const res = await CompanionRoomApi.getRoomFiles({
        spaceId: this.spaceId,
        chatRoomId: this.chatRoomId,
        size: 30,
      });
      const pdfs = (res.list ?? []).filter(
        (f) => String(f.messageType).toUpperCase() === "PDF"
      );
      runInAction(() => {
        this.pdfFiles = pdfs;
        this.filesHasMore = res.hasMore === true;
        this.filesCursorSentAt = res.nextCursorSentAt ?? null;
        this.filesCursorMessageId = res.nextCursorMessageId ?? null;
      });
    } finally {
      runInAction(() => {
        this.filesLoading = false;
      });
    }
  }

  async loadMoreFiles(): Promise<void> {
    if (!this.chatRoomId || !this.spaceId || !this.filesHasMore) return;
    if (
      this.filesLoadingMore ||
      !this.filesCursorSentAt ||
      !this.filesCursorMessageId
    ) {
      return;
    }
    runInAction(() => {
      this.filesLoadingMore = true;
    });
    try {
      const res = await CompanionRoomApi.getRoomFiles({
        spaceId: this.spaceId,
        chatRoomId: this.chatRoomId,
        cursorSentAt: this.filesCursorSentAt,
        cursorMessageId: this.filesCursorMessageId,
        size: 30,
      });
      const pdfs = (res.list ?? []).filter(
        (f) => String(f.messageType).toUpperCase() === "PDF"
      );
      runInAction(() => {
        this.pdfFiles = [...this.pdfFiles, ...pdfs];
        this.filesHasMore = res.hasMore === true;
        this.filesCursorSentAt = res.nextCursorSentAt ?? null;
        this.filesCursorMessageId = res.nextCursorMessageId ?? null;
      });
    } finally {
      runInAction(() => {
        this.filesLoadingMore = false;
      });
    }
  }

  async refreshPinned(opts?: { silent?: boolean }): Promise<void> {
    if (!this.chatRoomId || !this.spaceId) return;
    const silent = opts?.silent === true;
    if (!silent) {
      runInAction(() => {
        this.pinnedLoading = true;
      });
    }
    try {
      const res = await CompanionRoomApi.getTopPinnedMessages({
        spaceId: this.spaceId,
        chatRoomId: this.chatRoomId,
      });
      const list = res.list ?? [];
      runInAction(() => {
        this.pinnedList = list;
      });
    } catch {
      // 请求失败时 post 已 toast；避免未捕获的 Promise
    } finally {
      if (!silent) {
        runInAction(() => {
          this.pinnedLoading = false;
        });
      }
    }
  }

  private outboxStorageKey(): string | null {
    const uid = this.userId.trim();
    if (!uid || !this.spaceId.trim() || !this.chatRoomId.trim()) {
      return null;
    }
    return buildCompanionOutboxStorageKey(uid, this.spaceId, this.chatRoomId);
  }

  private persistOutbox(): void {
    const key = this.outboxStorageKey();
    if (!key) return;
    persistCompanionOutboxFromState(key, this.outboundRetryByMessageId, this.chatMessages);
  }

  /** 刷新后恢复未送达成功的本地外发（localStorage，按 userId+spaceId+chatRoomId） */
  private mergePersistedOutbox(): void {
    const key = this.outboxStorageKey();
    if (!key) return;
    const records = readCompanionOutbox(key);
    if (!records.length) return;
    runInAction(() => {
      const have = new Set(this.chatMessages.map((m) => m.id));
      const toAdd: CompanionMessageRow[] = [];
      for (const r of records) {
        this.outboundRetryByMessageId.set(r.tempId, r.payload);
        if (!have.has(r.tempId)) {
          toAdd.push({ ...r.row, outboundFailed: true });
          have.add(r.tempId);
        }
      }
      if (toAdd.length) {
        this.chatMessages = [...this.chatMessages, ...toAdd].sort((a, b) =>
          cmpChatOrder(a, b)
        );
      }
    });
  }

  dispose(): void {
    this.stopPoll();
    this.outboundRetryByMessageId.clear();
  }
}
